import { useMemo } from 'react'
import Big, { BigSource } from 'big.js'
import { useIntlContext } from '../components/IntlProviders.context'

export const getPercentage = (value = 0, max = 0, min = 0) =>
  value > 0 && max > 0 ? ((value - min) * 100) / (max - min) : 0

const toBig = (v?: BigSource) => {
  try {
    return Big(v || 0)
  } catch {
    return Big(0)
  }
}

// e.g. 0.015 -> 1.5
export const percentToFloat = (perc?: BigSource) =>
  parseFloat(
    toBig(perc || 0)
      .mul(100)
      .toString()
  )

// e.g. 1.5 -> 0.015
export const floatToPercent = (float?: BigSource) =>
  parseFloat(
    toBig(float || 0)
      .div(100)
      .toString()
  )

/**
 * limit NumberFormatOptions to just decimal and percent, as units are not used and currency is handled specifically
 * in currencyUtils.ts
 */
export type LimitedNumberFormatOptions = Omit<
  Intl.NumberFormatOptions & { style?: 'decimal' | 'percent' | undefined },
  'currency' | 'currencyDisplay' | 'currencySign' | 'unit' | 'unitDisplay'
>

/**
 * useFormatNumberLocale
 * This hook will return a number formatting function based on the user's locale.
 * @param options {LimitedNumberFormatOptions} Intl.NumberFormat options, limited to non-currency and non-unit options
 * for the formatter to use
 */
export const useNumberFormatter = (options: LimitedNumberFormatOptions = {}) => {
  const intl = useIntlContext().intl

  return useMemo(() => {
    return new Intl.NumberFormat(intl.locale, options)
  }, [intl, options])
}

export const getHumanReadableFloat = (number: number | null) => {
  /*
  params: number: any floating point value
  returns: a list of size 2.
           - 0th index has the minified value of the input number
           - 1st index will contain a string that represents units/orders of magnitude {k: 1000, M: million, B: billion}
   */
  if (number === 0 || number == null || Number.isNaN(number)) return [0]

  const i = Math.floor(Math.log(number) / Math.log(1000))
  let readableNumber: number | string = number / 1000 ** i

  // true if integer part of the readable number != fractional value of the readable number (10 != 10.12)
  // Fix fractional value to 2 because we consider 10.00123 = 10.00 = 10
  const requiresDecimal = Math.round(readableNumber) !== Number(readableNumber.toFixed(2))
  readableNumber = readableNumber.toFixed(requiresDecimal ? 2 : 0)

  return [readableNumber, ['', 'K', 'M', 'B', 'T'][i]]
}
